import React, { useEffect, useRef, useState } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './HandOrderGrantBoxOverlay.css';

const CLOSED_MS = 350;
const OPEN_HOLD_MS = 900;

/**
 * Box open VFX anchored to a battlefield slot when a unit grants an order to hand.
 */
export const HandOrderGrantBoxOverlay = ({ grant, onComplete }) => {
  const [position, setPosition] = useState(null);
  const [phase, setPhase] = useState('closed');
  const soundPlayedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!grant?.zone || grant.index == null) {
      return undefined;
    }

    const findSlotPosition = () => {
      const slotSelector = `[data-slot-zone="${grant.zone}"][data-slot-index="${grant.index}"]`;
      const slotElement = document.querySelector(slotSelector);
      if (!slotElement) {
        return null;
      }
      const rect = slotElement.getBoundingClientRect();
      return {
        left: rect.left + rect.width / 2,
        top: rect.top + rect.height / 2,
      };
    };

    const rafId = requestAnimationFrame(() => {
      const timeoutId = setTimeout(() => {
        const slotPos = findSlotPosition();
        if (!slotPos) {
          console.warn(
            `HandOrderGrantBoxOverlay: slot not found for ${grant.zone}[${grant.index}]`
          );
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
          return;
        }
        setPosition(slotPos);
      }, 50);

      return () => clearTimeout(timeoutId);
    });

    return () => cancelAnimationFrame(rafId);
  }, [grant]);

  useEffect(() => {
    if (!position) {
      return undefined;
    }

    const openTimer = setTimeout(() => {
      setPhase('open');
      if (!soundPlayedRef.current) {
        soundPlayedRef.current = true;
        playSoundEffect('/sounds/open_box.mp3', 0.85);
      }
    }, CLOSED_MS);

    const completeTimer = setTimeout(() => {
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    }, CLOSED_MS + OPEN_HOLD_MS);

    return () => {
      clearTimeout(openTimer);
      clearTimeout(completeTimer);
    };
  }, [position]);

  if (!grant || !position) {
    return null;
  }

  const imageSrc =
    phase === 'open' ? '/images/box_opened.png' : '/images/box_closed.png';

  return (
    <div
      className={`hand-order-grant-box-overlay${phase === 'open' ? ' is-open' : ''}`}
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
      aria-hidden="true"
    >
      <img src={imageSrc} alt="" className="hand-order-grant-box-overlay__image" draggable={false} />
    </div>
  );
};
